/**
 * Action Configuration Registry
 *
 * Central registry of all action configurations. Replaces heavy processor classes
 * with lightweight metadata + optional customization hooks.
 *
 * Organization:
 * - Auth: LOGIN, LOGOUT
 * - User Management: GET_ORGANIZATION_USERS, RESET_USER_PASSWORD, UPDATE_USER
 * - Organization: GET_ALL_ORGANIZATIONS, SWITCH_ORGANIZATION
 * - Board: All board/card/comment actions
 * - Dashboard: GET_DASHBOARD_STATS
 * - Notifications: GET_NOTIFICATIONS
 * - Test: NO_OP
 */

import { Action } from '@/business/types/action.types';
import { ActionConfig, ProgressMessages } from './action-config.types';
import { AsyncRequestStatus } from '@/business/types/async.types';
import { ValidationError } from '@/business/types/error.types';
import { ValidationResult } from '@/business/types/processor.types';
import StorageService from '@/utils/storage';
import { StorageKey } from '@/types/storage';
import { UserPreferences } from '@/types/preferences.types';
import { UserRole } from '@/types/enums/UserRole';
import { BoardTaskPriority } from '@/types/enums/BoardTaskPriority';
import { BoardTaskReflection } from '@/types/enums/BoardTaskReflection';

// Import generated validators
import {
  validateLoginInput,
  validateGetTaskInput,
  validateUpdateTaskInput,
  validateCreateBoardInput,
  validateCreateTaskInput,
  validateCreateCommentInput,
  validateGetAllBoardsInput,
  validateGetAllOrganizationsInput,
  validateGetBoardInput,
  validateGetBoardPreferencesInput,
  validateGetTaskAssignmentsInput,
  validateGetCommentsInput,
  validateGetDashboardStatsInput,
  validateGetNotificationsInput,
  validateGetOrganizationUsersInput,
  validateGetPresignedUploadUrlInput,
  validateListBoardBackgroundsInput,
  validateListProfilePicturesInput,
  validateResetUserPasswordInput,
  validateSwitchOrganizationInput,
  validateUpdateTaskStatusInput,
  validateUpdateProfilePictureInput,
  validateUpdateUserInput,
  validateUpdateUserPreferencesInput,
  validateUpdateBoardBackgroundInput,
  validateUpdateBoardStatusConfigInput,
  validateGetBoardTemplatesInput,
  validateCreateBoardTemplateInput,
  validateUpdateBoardTemplateInput,
  validateSaveBoardAsTemplateInput,
  validateCreateBoardFromTemplateInput,
  validateUpdateBoardInput,
  validateGetOrgConfigurationsInput,
  validateUpdateOrganizationInput,
  validateUpdateOrgConfigurationInput,
  validateUpdateOrgProfilePictureInput,
  validateListOrgProfilePicturesInput,
  validateDeleteBoardInput,
  validateDeleteTaskInput,
  validateCreateOrganizationInput,
  validateDeleteOrganizationInput,
  validateRestoreOrganizationInput,
  validateDeleteBoardTemplateInput,
  validateRestoreBoardTemplateInput,
  validateCreateBoardStatusInput,
  validateDeleteBoardStatusInput,
  validateRestoreBoardStatusInput,
  validateRestoreBoardInput,
  validateRestoreTaskInput,
  validateMarkNotificationReadInput,
  validateCreateUserInput,
  validateDeleteUserInput,
  validateGetScheduledJobsInput,
  validateCreateScheduledJobInput,
  validateUpdateScheduledJobInput,
  validateGetJobRunInput,
  validateGetJobRunsInput,
  validateRunScheduledJobInput,
  validateCancelJobRunInput,
  validateSearchUsersInput,
  validateCreateUserGroupInput,
  validateDeleteUserGroupInput,
  validateGetUserGroupsInput,
  validateAddUserGroupMemberInput,
  validateBulkAddUserGroupMembersInput,
  validateRemoveUserGroupMemberInput,
  validateGetUserGroupMembersInput,
  validateGetUserGroupMembersBulkInput,
} from '@/types/generated/validators';

// Import types for transformations
import type { LoginInput } from '@/types/generated';
import type { GetBoardInput } from '@/types/generated';
import type { UpdateUserInput } from '@/types/generated';
import type { GetPreferencesOutput } from '@/types/generated';
import type {
  ListProfilePicturesOutput,
  ListOrgProfilePicturesOutput,
  ListBoardBackgroundsOutput,
  ListBoardBackgroundsInput,
} from '@/types/generated';
import { AuthUser, LoginUIOutput } from '@/types/auth';
import { GetBoardUIOutput, GetCardUIOutput } from '@/types/action-outputs';
import {
  BackendLoginResponse,
  BackendGetBoardResponse,
  BackendGetCardResponse,
} from '@/types/backend-types';
import { Board } from '@/types/models/Board';
import { BoardTask } from '@/types/models/BoardTask';
import {
  BoardStatusWithCards,
  BoardTaskWithAssignments,
} from '@/types/board-config.types';
import { logger } from '@/utils/logger';
import { queryClient } from '@/lib/query-client';
import { queryKeys } from '@/lib/query-keys';

// ============================================================================
// HELPERS
// ============================================================================

/** Wraps a generated validator into a standard ValidationResult function */
function wrapValidator(validateFn: (data: unknown) => boolean) {
  return (data: unknown): ValidationResult => {
    if (!validateFn(data)) {
      return {
        isValid: false,
        errors: [
          new ValidationError('Invalid input structure', 'data', 'type'),
        ],
      };
    }
    return { isValid: true };
  };
}

/** Generates standard progress messages from a base verb and noun */
function progress(baseVerb: string, noun: string): ProgressMessages {
  let ingForm: string;
  if (baseVerb.endsWith('e')) {
    ingForm = baseVerb.slice(0, -1) + 'ing';
  } else {
    ingForm = baseVerb + 'ing';
  }

  let pastForm: string;
  if (baseVerb.endsWith('e')) {
    pastForm = baseVerb + 'd';
  } else {
    pastForm = baseVerb + 'ed';
  }

  const ingCap = ingForm.charAt(0).toUpperCase() + ingForm.slice(1);
  const nounLower = noun.charAt(0).toLowerCase() + noun.slice(1);

  return {
    [AsyncRequestStatus.SUBMITTED]: 'Submitted to backend...',
    [AsyncRequestStatus.IN_PROGRESS]: `${ingCap} ${nounLower}...`,
    [AsyncRequestStatus.COMPLETED]: `${noun} ${pastForm}`,
    [AsyncRequestStatus.FAILED]: `Failed to ${baseVerb} ${nounLower}`,
  };
}

// ============================================================================
// AUTH ACTIONS
// ============================================================================

/**
 * LOGIN - Authenticates user and stores session
 * Special: transformResponse (flatten organization), executeSideEffect (store auth + fetch profile)
 */
export const LOGIN_CONFIG: ActionConfig<
  LoginInput,
  BackendLoginResponse,
  LoginUIOutput
> = {
  action: Action.LOGIN,
  progressMessages: {
    [AsyncRequestStatus.SUBMITTED]: 'Submitted to backend...',
    [AsyncRequestStatus.IN_PROGRESS]: 'Authenticating...',
    [AsyncRequestStatus.COMPLETED]: 'Login complete',
    [AsyncRequestStatus.FAILED]: 'Authentication failed',
  },
  validate: (data: LoginInput) => {
    if (!validateLoginInput(data)) {
      return {
        isValid: false,
        errors: [
          new ValidationError(
            'Invalid login data structure or format',
            'data',
            'type'
          ),
        ],
      };
    }
    return { isValid: true };
  },
  transformResponse: (backend: BackendLoginResponse): LoginUIOutput => {
    const user: AuthUser = {
      id: backend.user.id,
      username: backend.user.username,
      display_name: backend.user.display_name,
      email: backend.user.email || '',
      role: backend.user.role as UserRole,
      organization_id: backend.user.organization_id,
      organization_code: backend.user.organization_code,
      organization_title: backend.user.organization?.title || '',
      profile_image: backend.user.profile_image,
      organization: backend.user.organization
        ? {
            ...backend.user.organization,
          }
        : {
            id: backend.user.organization_id,
            code: backend.user.organization_code,
            title: '',
            is_superadmin_org: false,
          },
    };

    return {
      user,
      token: backend.token,
    };
  },
  executeSideEffect: async (data: LoginUIOutput) => {
    // Persist user data and tokens to storage
    StorageService.set(StorageKey.USER, data.user);
    StorageService.set(StorageKey.AUTH_TOKEN, data.token.access_token);

    // Fetch and cache profile data (silent failure - don't block login)
    try {
      // Dynamic import to avoid circular dependency
      const { actionOrchestrator } =
        await import('@/business/orchestrator/ActionOrchestrator');
      await actionOrchestrator.execute<
        Record<string, never>,
        GetPreferencesOutput
      >(
        Action.GET_PREFERENCES,
        {},
        {
          toast: false, // Silent - preferences are supplementary data
        }
      );
    } catch (error) {
      // Silent failure - profile data is non-critical for login
      // User can still use the app, profile will be fetched on next request
      logger.warn('Failed to fetch profile after login:', { error });
    }
  },
};

/**
 * LOGOUT - Clears user session
 * Special: executeSideEffect (clear storage)
 */
export const LOGOUT_CONFIG: ActionConfig = {
  action: Action.LOGOUT,
  progressMessages: {
    [AsyncRequestStatus.SUBMITTED]: 'Starting logout...',
    [AsyncRequestStatus.IN_PROGRESS]: 'Clearing local data...',
    [AsyncRequestStatus.COMPLETED]: 'Logout complete',
    [AsyncRequestStatus.FAILED]: 'Logout failed',
  },
  validate: () => ({ isValid: true }), // No input required
  executeSideEffect: async () => {
    // Clear all storage to ensure data privacy
    StorageService.clearAll();
  },
};

// ============================================================================
// USER MANAGEMENT ACTIONS
// ============================================================================

/**
 * GET_PREFERENCES - Fetches user preferences data including theme, mood, sizing
 * Special: executeSideEffect (cache user data with profile_image + preferences separately)
 */
export const GET_PREFERENCES_CONFIG: ActionConfig<
  Record<string, never>,
  unknown,
  GetPreferencesOutput
> = {
  action: Action.GET_PREFERENCES,
  progressMessages: progress('load', 'Preferences'),
  validate: () => ({ isValid: true }), // No input required
  executeSideEffect: async (data: GetPreferencesOutput) => {
    // Merge preferences into existing user instead of overwriting
    // This preserves organization_code/organization_title from LOGIN
    const existingUser = StorageService.get<AuthUser>(StorageKey.USER);
    if (existingUser) {
      const mergedUser: AuthUser = {
        ...existingUser,
        email: data.email ?? existingUser.email,
        organization_id: data.organization_id ?? existingUser.organization_id,
        profile_image:
          data.profile?.profile_image ?? existingUser.profile_image,
        organization: data.organization ?? existingUser.organization,
      };
      StorageService.set(StorageKey.USER, mergedUser);
    }

    // Preferences data (no profile_image - it's in user)
    const preferences: UserPreferences = {
      theme: data.profile?.theme,
      mood: data.profile?.mood,
      sizing: data.profile?.sizing,
      playful_theme: data.profile?.playful_theme,
      playful_sound: data.profile?.playful_sound,
    };

    StorageService.set(StorageKey.USER_PREFERENCES, preferences);

    // Update React Query cache — ThemeContext/SizingContext subscribe via usePreferencesQuery
    queryClient.setQueryData(queryKeys.userPreferences.all(), preferences);
  },
};

export const LIST_BOARD_BACKGROUNDS_CONFIG: ActionConfig<
  ListBoardBackgroundsInput,
  unknown,
  ListBoardBackgroundsOutput
> = {
  action: Action.LIST_BOARD_BACKGROUNDS,
  progressMessages: {
    [AsyncRequestStatus.SUBMITTED]: 'Loading backgrounds...',
    [AsyncRequestStatus.IN_PROGRESS]: 'Fetching from storage...',
    [AsyncRequestStatus.COMPLETED]: 'Backgrounds loaded',
    [AsyncRequestStatus.FAILED]: 'Failed to load backgrounds',
  },
  validate: wrapValidator(validateListBoardBackgroundsInput),
  executeSideEffect: async (
    data: ListBoardBackgroundsOutput,
    input: ListBoardBackgroundsInput
  ) => {
    const boardId = input.board_id;
    const existing =
      StorageService.get<
        Record<string, ListBoardBackgroundsOutput['backgrounds']>
      >(StorageKey.BOARD_BACKGROUNDS) ?? {};
    StorageService.set(StorageKey.BOARD_BACKGROUNDS, {
      ...existing,
      [boardId]: data.backgrounds,
    });
    queryClient.setQueryData(
      queryKeys.boardBackgrounds.forBoard(boardId),
      data
    );
  },
};

export const LIST_PROFILE_PICTURES_CONFIG: ActionConfig<
  unknown,
  unknown,
  ListProfilePicturesOutput
> = {
  action: Action.LIST_PROFILE_PICTURES,
  progressMessages: {
    [AsyncRequestStatus.SUBMITTED]: 'Loading profile pictures...',
    [AsyncRequestStatus.IN_PROGRESS]: 'Fetching from storage...',
    [AsyncRequestStatus.COMPLETED]: 'Profile pictures loaded',
    [AsyncRequestStatus.FAILED]: 'Failed to load profile pictures',
  },
  validate: wrapValidator(validateListProfilePicturesInput),
  executeSideEffect: async (data: ListProfilePicturesOutput) => {
    StorageService.set(StorageKey.PROFILE_PICTURES, data.pictures);
    queryClient.setQueryData(queryKeys.profilePictures.all(), data);
  },
};

export const UPDATE_PROFILE_PICTURE_CONFIG: ActionConfig = {
  action: Action.UPDATE_PROFILE_PICTURE,
  progressMessages: progress('update', 'Profile picture'),
  validate: wrapValidator(validateUpdateProfilePictureInput),
};

export const UPDATE_USER_PREFERENCES_CONFIG: ActionConfig = {
  action: Action.UPDATE_USER_PREFERENCES,
  progressMessages: progress('update', 'Preferences'),
  validate: wrapValidator(validateUpdateUserPreferencesInput),
};

export const GET_ORGANIZATION_USERS_CONFIG: ActionConfig = {
  action: Action.GET_ORGANIZATION_USERS,
  progressMessages: progress('load', 'Users'),
  validate: wrapValidator(validateGetOrganizationUsersInput),
};

export const SEARCH_USERS_CONFIG: ActionConfig = {
  action: Action.SEARCH_USERS,
  progressMessages: progress('search', 'Users'),
  validate: wrapValidator(validateSearchUsersInput),
};

export const GET_USER_PROFILE_CONFIG: ActionConfig = {
  action: Action.GET_USER_PROFILE,
  progressMessages: progress('load', 'Profile'),
  validate: (data: unknown): ValidationResult => {
    if (typeof data !== 'object' || data === null) {
      return {
        isValid: false,
        errors: [new ValidationError('Invalid input', 'data', 'type')],
      };
    }
    const d = data as Record<string, unknown>;
    if (typeof d.user_id !== 'string' || !d.user_id) {
      return {
        isValid: false,
        errors: [
          new ValidationError('user_id is required', 'user_id', 'required'),
        ],
      };
    }
    return { isValid: true };
  },
};

export const RESET_USER_PASSWORD_CONFIG: ActionConfig = {
  action: Action.RESET_USER_PASSWORD,
  progressMessages: {
    [AsyncRequestStatus.SUBMITTED]: 'Submitted to backend...',
    [AsyncRequestStatus.IN_PROGRESS]: 'Resetting password...',
    [AsyncRequestStatus.COMPLETED]: 'Password reset',
    [AsyncRequestStatus.FAILED]: 'Failed to reset password',
  },
  validate: wrapValidator(validateResetUserPasswordInput),
  executeSideEffect: async () => {
    // Invalidate all org user queries — the active org query key is unknown here
    queryClient.invalidateQueries({ queryKey: queryKeys.orgUsers.all() });
  },
};

/**
 * UPDATE_USER - Updates user information
 * Special: validate (email/username/role rules)
 */
export const UPDATE_USER_CONFIG: ActionConfig<UpdateUserInput> = {
  action: Action.UPDATE_USER,
  progressMessages: progress('update', 'User'),
  validate: (data: UpdateUserInput): ValidationResult => {
    const errors: ValidationError[] = [];

    // Use generated validator for basic type checking
    if (!validateUpdateUserInput(data)) {
      errors.push(new ValidationError('Invalid input data', 'data', 'invalid'));
      return { isValid: false, errors };
    }

    // Additional business validation beyond generated validator
    // Check for invalid characters in username (database constraint)
    if (data.username !== undefined) {
      const invalidChars = /[.\-_\s@!#$%^&*()]/;
      if (invalidChars.test(data.username)) {
        errors.push(
          new ValidationError(
            'Username cannot contain special characters, spaces, or punctuation',
            'username',
            'invalid'
          )
        );
      }
    }

    // Validate email format if provided
    if (data.email !== undefined && data.email !== null) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(data.email)) {
        errors.push(
          new ValidationError('Invalid email format', 'email', 'invalid')
        );
      }
    }

    // Validate role if provided
    if (data.role !== undefined) {
      const validRoles = Object.values(UserRole);
      if (!validRoles.includes(data.role as UserRole)) {
        errors.push(
          new ValidationError(
            `Invalid role. Must be one of: ${validRoles.join(', ')}`,
            'role',
            'invalid'
          )
        );
      }
    }

    return {
      isValid: errors.length === 0,
      errors: errors.length > 0 ? errors : undefined,
    };
  },
  executeSideEffect: async () => {
    // Invalidate all org user queries — the active org query key is unknown here
    queryClient.invalidateQueries({ queryKey: queryKeys.orgUsers.all() });
  },
};

export const CREATE_USER_CONFIG: ActionConfig = {
  action: Action.CREATE_USER,
  progressMessages: progress('create', 'User'),
  validate: wrapValidator(validateCreateUserInput),
  executeSideEffect: async () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.orgUsers.all() });
  },
};

export const DELETE_USER_CONFIG: ActionConfig = {
  action: Action.DELETE_USER,
  progressMessages: progress('delete', 'User'),
  validate: wrapValidator(validateDeleteUserInput),
  executeSideEffect: async () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.orgUsers.all() });
  },
};

// ============================================================================
// STORAGE ACTIONS
// ============================================================================

export const GET_PRESIGNED_UPLOAD_URL_CONFIG: ActionConfig = {
  action: Action.GET_PRESIGNED_UPLOAD_URL,
  progressMessages: progress('generate', 'Upload URL'),
  validate: wrapValidator(validateGetPresignedUploadUrlInput),
};

// ============================================================================
// ORGANIZATION ACTIONS
// ============================================================================

export const GET_ALL_ORGANIZATIONS_CONFIG: ActionConfig = {
  action: Action.GET_ALL_ORGANIZATIONS,
  progressMessages: progress('load', 'Organizations'),
  validate: wrapValidator(validateGetAllOrganizationsInput),
};

export const CREATE_ORGANIZATION_CONFIG: ActionConfig = {
  action: Action.CREATE_ORGANIZATION,
  progressMessages: progress('create', 'Organization'),
  validate: wrapValidator(validateCreateOrganizationInput),
  executeSideEffect: async () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.allOrgs.all() });
  },
};

export const DELETE_ORGANIZATION_CONFIG: ActionConfig = {
  action: Action.DELETE_ORGANIZATION,
  progressMessages: progress('delete', 'Organization'),
  validate: wrapValidator(validateDeleteOrganizationInput),
  executeSideEffect: async () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.allOrgs.all() });
  },
};

export const RESTORE_ORGANIZATION_CONFIG: ActionConfig = {
  action: Action.RESTORE_ORGANIZATION,
  progressMessages: progress('restore', 'Organization'),
  validate: wrapValidator(validateRestoreOrganizationInput),
  executeSideEffect: async () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.allOrgs.all() });
  },
};

export const SWITCH_ORGANIZATION_CONFIG: ActionConfig = {
  action: Action.SWITCH_ORGANIZATION,
  progressMessages: progress('switch', 'Organization'),
  validate: wrapValidator(validateSwitchOrganizationInput),
};

// ============================================================================
// BOARD ACTIONS
// ============================================================================

/**
 * GET_BOARD - Fetches board with statuses and cards
 * Special: validate (mutual exclusivity), transformResponse (nested restructure)
 */
export const GET_BOARD_CONFIG: ActionConfig<
  GetBoardInput,
  BackendGetBoardResponse,
  GetBoardUIOutput
> = {
  action: Action.GET_BOARD,
  progressMessages: progress('load', 'Board data'),
  validate: (data: GetBoardInput): ValidationResult => {
    if (!validateGetBoardInput(data)) {
      return {
        isValid: false,
        errors: [
          new ValidationError(
            'Invalid get board data structure or format',
            'data',
            'type'
          ),
        ],
      };
    }

    // Additional business logic validation
    const errors: ValidationError[] = [];

    // Must provide either board_id or user_id (but not both, not neither)
    if (!data.board_id && !data.user_id) {
      errors.push(
        new ValidationError(
          'Either board_id or user_id is required',
          'board_id',
          'required'
        )
      );
    }

    if (data.board_id && data.user_id) {
      errors.push(
        new ValidationError(
          'Provide either board_id or user_id, not both',
          'board_id',
          'invalid'
        )
      );
    }

    return {
      isValid: errors.length === 0,
      errors: errors.length > 0 ? errors : undefined,
    };
  },
  transformResponse: (backend: BackendGetBoardResponse): GetBoardUIOutput => {
    const board: Board = {
      id: backend.board.id,
      title: backend.board.title,
      organization_id: backend.board.organization_id,
      owner_id: backend.board.owner_id,
      template_id: backend.board.template_id,
      group_id: backend.board.group_id,
      created_at: backend.board.created_at,
      updated_at: backend.board.updated_at,
      deleted_at: backend.board.deleted_at,
    };

    const statuses: BoardStatusWithCards[] = backend.statuses.map(status => {
      const cards: BoardTaskWithAssignments[] = status.tasks.map(card => ({
        id: card.id,
        board_id: card.board_id,
        organization_id: card.organization_id,
        status_id: card.status_id,
        title: card.title,
        description: card.description,
        metadata: card.metadata,
        created_by: card.created_by,
        created_at: card.created_at,
        updated_at: card.updated_at,
        start_date: card.start_date,
        priority: card.priority as BoardTaskPriority | undefined,
        estimated_pomodoros: card.estimated_pomodoros,
        actual_pomodoros: card.actual_pomodoros,
        reflection: card.reflection as BoardTaskReflection | undefined,
        assignee_ids: card.assignee_ids,
        mentor_ids: card.mentor_ids,
      }));

      return {
        id: status.id,
        board_id: status.board_id,
        name: status.name,
        position: status.position,
        color: status.color,
        config: status.config,
        created_at: status.created_at,
        updated_at: status.updated_at,
        cards,
      };
    });

    return { board, statuses };
  },
};

export const GET_ALL_BOARDS_CONFIG: ActionConfig = {
  action: Action.GET_ALL_BOARDS,
  progressMessages: progress('load', 'Boards'),
  validate: wrapValidator(validateGetAllBoardsInput),
};

export const GET_BOARD_PREFERENCES_CONFIG: ActionConfig = {
  action: Action.GET_BOARD_PREFERENCES,
  progressMessages: progress('load', 'Board preferences'),
  validate: wrapValidator(validateGetBoardPreferencesInput),
};

/**
 * GET_CARD - Fetches single card details
 * Special: transformResponse (wrap in card object)
 */
export const GET_CARD_CONFIG: ActionConfig<
  unknown,
  BackendGetCardResponse,
  GetCardUIOutput
> = {
  action: Action.GET_TASK,
  progressMessages: progress('load', 'Card'),
  validate: wrapValidator(validateGetTaskInput),
  transformResponse: (backend: BackendGetCardResponse): GetCardUIOutput => {
    const card: BoardTask = {
      id: backend.card.id,
      board_id: backend.card.board_id,
      organization_id: backend.card.organization_id,
      status_id: backend.card.status_id,
      title: backend.card.title,
      description: backend.card.description,
      metadata: backend.card.metadata,
      created_by: backend.card.created_by,
      created_at: backend.card.created_at,
      updated_at: backend.card.updated_at,
      start_date: backend.card.start_date,
      priority: backend.card.priority as BoardTaskPriority | undefined,
      estimated_pomodoros: backend.card.estimated_pomodoros,
      actual_pomodoros: backend.card.actual_pomodoros,
      reflection: backend.card.reflection as BoardTaskReflection | undefined,
      deleted_at: backend.card.deleted_at,
    };

    return { card };
  },
};

export const GET_CARD_ASSIGNMENTS_CONFIG: ActionConfig = {
  action: Action.GET_TASK_ASSIGNMENTS,
  progressMessages: progress('load', 'Assignments'),
  validate: wrapValidator(validateGetTaskAssignmentsInput),
};

export const CREATE_CARD_CONFIG: ActionConfig = {
  action: Action.CREATE_TASK,
  progressMessages: progress('create', 'Card'),
  validate: wrapValidator(validateCreateTaskInput),
  executeSideEffect: async (_data: any, input: any) => {
    if (input?.board_id) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.board.detail({ board_id: input.board_id }),
      });
    } else {
      queryClient.invalidateQueries({ queryKey: queryKeys.board.all() });
    }
  },
};

export const UPDATE_CARD_CONFIG: ActionConfig = {
  action: Action.UPDATE_TASK,
  progressMessages: progress('update', 'Card'),
  validate: wrapValidator(validateUpdateTaskInput),
  executeSideEffect: async (_data: any, input: any) => {
    if (input?.board_id) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.board.detail({ board_id: input.board_id }),
      });
    } else {
      queryClient.invalidateQueries({ queryKey: queryKeys.board.all() });
    }
  },
};

export const UPDATE_CARD_STATUS_CONFIG: ActionConfig = {
  action: Action.UPDATE_TASK_STATUS,
  progressMessages: progress('move', 'Card'),
  validate: wrapValidator(validateUpdateTaskStatusInput),
  executeSideEffect: async (_data: any, input: any) => {
    if (input?.board_id) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.board.detail({ board_id: input.board_id }),
      });
    } else {
      queryClient.invalidateQueries({ queryKey: queryKeys.board.all() });
    }
  },
};

export const UPDATE_BOARD_BACKGROUND_CONFIG: ActionConfig = {
  action: Action.UPDATE_BOARD_BACKGROUND,
  progressMessages: progress('update', 'Board background'),
  validate: wrapValidator(validateUpdateBoardBackgroundInput),
  executeSideEffect: async (_data: any, input: any) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.board.all() });
    if (input?.board_id) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.boardPreferences.forBoard(input.board_id),
      });
    } else {
      queryClient.invalidateQueries({
        queryKey: queryKeys.boardPreferences.all(),
      });
    }
  },
};

export const UPDATE_BOARD_STATUS_CONFIG_CONFIG: ActionConfig = {
  action: Action.UPDATE_BOARD_STATUS_CONFIG,
  progressMessages: progress('update', 'Column rules'),
  validate: wrapValidator(validateUpdateBoardStatusConfigInput),
  executeSideEffect: async (_data: any, input: any) => {
    queryClient.invalidateQueries({ queryKey: queryKeys.board.all() });
    if (input?.board_id) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.boardPreferences.forBoard(input.board_id),
      });
    } else {
      queryClient.invalidateQueries({
        queryKey: queryKeys.boardPreferences.all(),
      });
    }
  },
};

export const CREATE_COMMENT_CONFIG: ActionConfig = {
  action: Action.CREATE_COMMENT,
  progressMessages: progress('create', 'Comment'),
  validate: wrapValidator(validateCreateCommentInput),
  executeSideEffect: async (data: any) => {
    if (data?.task_id) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.comments.forTask(data.task_id),
      });
    }
  },
};

export const GET_COMMENTS_CONFIG: ActionConfig = {
  action: Action.GET_COMMENTS,
  progressMessages: progress('load', 'Comments'),
  validate: wrapValidator(validateGetCommentsInput),
};

export const CREATE_BOARD_CONFIG: ActionConfig = {
  action: Action.CREATE_BOARD,
  progressMessages: progress('create', 'Board'),
  validate: wrapValidator(validateCreateBoardInput),
  executeSideEffect: async () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.board.all() });
  },
};

export const GET_CARD_RELATIONS_CONFIG: ActionConfig = {
  action: Action.GET_TASK_RELATIONS,
  progressMessages: {
    [AsyncRequestStatus.SUBMITTED]: 'Fetching relations...',
    [AsyncRequestStatus.IN_PROGRESS]: 'Loading linked cards...',
    [AsyncRequestStatus.COMPLETED]: 'Relations loaded',
    [AsyncRequestStatus.FAILED]: 'Failed to load relations',
  },
};

export const CREATE_CARD_RELATION_CONFIG: ActionConfig = {
  action: Action.CREATE_TASK_RELATION,
  progressMessages: {
    [AsyncRequestStatus.SUBMITTED]: 'Creating link...',
    [AsyncRequestStatus.IN_PROGRESS]: 'Linking cards...',
    [AsyncRequestStatus.COMPLETED]: 'Cards linked',
    [AsyncRequestStatus.FAILED]: 'Failed to link cards',
  },
  executeSideEffect: async (_data: any, input: any) => {
    if (input?.source_task_id) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.taskRelations.forTask(input.source_task_id),
      });
    } else {
      queryClient.invalidateQueries({
        queryKey: queryKeys.taskRelations.all(),
      });
    }
  },
};

export const DELETE_CARD_RELATION_CONFIG: ActionConfig = {
  action: Action.DELETE_TASK_RELATION,
  progressMessages: {
    [AsyncRequestStatus.SUBMITTED]: 'Removing link...',
    [AsyncRequestStatus.IN_PROGRESS]: 'Unlinking cards...',
    [AsyncRequestStatus.COMPLETED]: 'Link removed',
    [AsyncRequestStatus.FAILED]: 'Failed to remove link',
  },
  executeSideEffect: async (_data: any, input: any) => {
    if (input?.source_task_id) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.taskRelations.forTask(input.source_task_id),
      });
    } else {
      queryClient.invalidateQueries({
        queryKey: queryKeys.taskRelations.all(),
      });
    }
  },
};

// ============================================================================
// DELETE ACTIONS
// ============================================================================

export const DELETE_BOARD_CONFIG: ActionConfig = {
  action: Action.DELETE_BOARD,
  progressMessages: progress('delete', 'Board'),
  validate: wrapValidator(validateDeleteBoardInput),
  executeSideEffect: async () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.board.all() });
  },
};

export const DELETE_CARD_CONFIG: ActionConfig = {
  action: Action.DELETE_TASK,
  progressMessages: progress('delete', 'Card'),
  validate: wrapValidator(validateDeleteTaskInput),
  executeSideEffect: async (_data: any, input: any) => {
    if (input?.board_id) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.board.detail({ board_id: input.board_id }),
      });
    } else {
      queryClient.invalidateQueries({ queryKey: queryKeys.board.all() });
    }
  },
};

// ============================================================================
// BOARD STATUS/SWIMLANE ACTIONS
// ============================================================================

export const CREATE_BOARD_STATUS_CONFIG: ActionConfig = {
  action: Action.CREATE_BOARD_STATUS,
  progressMessages: progress('create', 'Column'),
  validate: wrapValidator(validateCreateBoardStatusInput),
  executeSideEffect: async (_data: any, input: any) => {
    if (input?.board_id) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.board.detail({ board_id: input.board_id }),
      });
    } else {
      queryClient.invalidateQueries({ queryKey: queryKeys.board.all() });
    }
  },
};

export const DELETE_BOARD_STATUS_CONFIG: ActionConfig = {
  action: Action.DELETE_BOARD_STATUS,
  progressMessages: progress('delete', 'Column'),
  validate: wrapValidator(validateDeleteBoardStatusInput),
  executeSideEffect: async () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.board.all() });
  },
};

export const RESTORE_BOARD_STATUS_CONFIG: ActionConfig = {
  action: Action.RESTORE_BOARD_STATUS,
  progressMessages: progress('restore', 'Column'),
  validate: wrapValidator(validateRestoreBoardStatusInput),
  executeSideEffect: async () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.board.all() });
  },
};

// ============================================================================
// RESTORE ACTIONS
// ============================================================================

export const RESTORE_BOARD_CONFIG: ActionConfig = {
  action: Action.RESTORE_BOARD,
  progressMessages: progress('restore', 'Board'),
  validate: wrapValidator(validateRestoreBoardInput),
  executeSideEffect: async () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.board.all() });
  },
};

export const RESTORE_CARD_CONFIG: ActionConfig = {
  action: Action.RESTORE_TASK,
  progressMessages: progress('restore', 'Card'),
  validate: wrapValidator(validateRestoreTaskInput),
  executeSideEffect: async () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.board.all() });
  },
};

export const DELETE_BOARD_TEMPLATE_CONFIG: ActionConfig = {
  action: Action.DELETE_BOARD_TEMPLATE,
  progressMessages: progress('delete', 'Template'),
  validate: wrapValidator(validateDeleteBoardTemplateInput),
  executeSideEffect: async () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.boardTemplates.all(),
    });
  },
};

export const RESTORE_BOARD_TEMPLATE_CONFIG: ActionConfig = {
  action: Action.RESTORE_BOARD_TEMPLATE,
  progressMessages: progress('restore', 'Template'),
  validate: wrapValidator(validateRestoreBoardTemplateInput),
  executeSideEffect: async () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.boardTemplates.all(),
    });
  },
};

// ============================================================================
// DASHBOARD ACTIONS
// ============================================================================

export const GET_DASHBOARD_STATS_CONFIG: ActionConfig = {
  action: Action.GET_DASHBOARD_STATS,
  progressMessages: progress('load', 'Statistics'),
  validate: wrapValidator(validateGetDashboardStatsInput),
};

export const GET_VELOCITY_STATS_CONFIG: ActionConfig = {
  action: Action.GET_LEARNING_PROGRESS_STATS,
  progressMessages: progress('load', 'LearningProgress Stats'),
};

export const GET_ACTIVITY_HEATMAP_CONFIG: ActionConfig = {
  action: Action.GET_MY_PROGRESS,
  progressMessages: progress('load', 'Activity Heatmap'),
};

// ============================================================================
// NOTIFICATION ACTIONS
// ============================================================================

export const GET_NOTIFICATIONS_CONFIG: ActionConfig = {
  action: Action.GET_NOTIFICATIONS,
  progressMessages: progress('load', 'Notifications'),
  validate: wrapValidator(validateGetNotificationsInput),
};

export const MARK_NOTIFICATION_READ_CONFIG: ActionConfig = {
  action: Action.MARK_NOTIFICATION_READ,
  progressMessages: progress('mark', 'Notification read'),
  validate: wrapValidator(validateMarkNotificationReadInput),
  executeSideEffect: async () => {
    queryClient.invalidateQueries({
      queryKey: ['notifications'],
    });
  },
};

// ============================================================================
// JOBS ACTIONS
// ============================================================================

export const GET_SCHEDULED_JOBS_CONFIG: ActionConfig = {
  action: Action.GET_SCHEDULED_JOBS,
  progressMessages: progress('load', 'Jobs'),
  validate: wrapValidator(validateGetScheduledJobsInput),
};

export const CREATE_SCHEDULED_JOB_CONFIG: ActionConfig = {
  action: Action.CREATE_SCHEDULED_JOB,
  progressMessages: progress('create', 'Job'),
  validate: wrapValidator(validateCreateScheduledJobInput),
};

export const UPDATE_SCHEDULED_JOB_CONFIG: ActionConfig = {
  action: Action.UPDATE_SCHEDULED_JOB,
  progressMessages: progress('update', 'Job'),
  validate: wrapValidator(validateUpdateScheduledJobInput),
};

export const RUN_SCHEDULED_JOB_CONFIG: ActionConfig = {
  action: Action.RUN_SCHEDULED_JOB,
  progressMessages: progress('run', 'Job'),
  validate: wrapValidator(validateRunScheduledJobInput),
};

export const GET_JOB_RUN_CONFIG: ActionConfig = {
  action: Action.GET_JOB_RUN,
  progressMessages: progress('load', 'Job Run'),
  validate: wrapValidator(validateGetJobRunInput),
};

export const GET_JOB_RUNS_CONFIG: ActionConfig = {
  action: Action.GET_JOB_RUNS,
  progressMessages: progress('load', 'Job Runs'),
  validate: wrapValidator(validateGetJobRunsInput),
};

export const CANCEL_JOB_RUN_CONFIG: ActionConfig = {
  action: Action.CANCEL_JOB_RUN,
  progressMessages: progress('cancel', 'Job Run'),
  validate: wrapValidator(validateCancelJobRunInput),
};

// ============================================================================
// ============================================================================
// USER GROUP ACTIONS
// ============================================================================

export const CREATE_USER_GROUP_CONFIG: ActionConfig = {
  action: Action.CREATE_USER_GROUP,
  progressMessages: progress('create', 'User Group'),
  validate: wrapValidator(validateCreateUserGroupInput),
};

export const DELETE_USER_GROUP_CONFIG: ActionConfig = {
  action: Action.DELETE_USER_GROUP,
  progressMessages: progress('delete', 'User Group'),
  validate: wrapValidator(validateDeleteUserGroupInput),
};

export const GET_USER_GROUPS_CONFIG: ActionConfig = {
  action: Action.GET_USER_GROUPS,
  progressMessages: progress('load', 'User Groups'),
  validate: wrapValidator(validateGetUserGroupsInput),
};

export const ADD_USER_GROUP_MEMBER_CONFIG: ActionConfig = {
  action: Action.ADD_USER_GROUP_MEMBER,
  progressMessages: progress('add', 'Group Member'),
  validate: wrapValidator(validateAddUserGroupMemberInput),
};

export const BULK_ADD_USER_GROUP_MEMBERS_CONFIG: ActionConfig = {
  action: Action.BULK_ADD_USER_GROUP_MEMBERS,
  progressMessages: progress('add', 'Group Members'),
  validate: wrapValidator(validateBulkAddUserGroupMembersInput),
};

export const REMOVE_USER_GROUP_MEMBER_CONFIG: ActionConfig = {
  action: Action.REMOVE_USER_GROUP_MEMBER,
  progressMessages: progress('remove', 'Group Member'),
  validate: wrapValidator(validateRemoveUserGroupMemberInput),
};

export const GET_USER_GROUP_MEMBERS_CONFIG: ActionConfig = {
  action: Action.GET_USER_GROUP_MEMBERS,
  progressMessages: progress('load', 'Group Members'),
  validate: wrapValidator(validateGetUserGroupMembersInput),
};

export const GET_USER_GROUP_MEMBERS_BULK_CONFIG: ActionConfig = {
  action: Action.GET_USER_GROUP_MEMBERS_BULK,
  progressMessages: progress('load', 'Group Members'),
  validate: wrapValidator(validateGetUserGroupMembersBulkInput),
};

// ============================================================================
// GROUP ASSIGNMENT ACTIONS
// ============================================================================

export const GET_COHORT_BOARD_CONFIG: ActionConfig = {
  action: Action.GET_COHORT_BOARD,
  progressMessages: progress('load', 'Cohort Board'),
  validate: () => ({ isValid: true }),
};

export const GET_COHORT_PROGRESS_CONFIG: ActionConfig = {
  action: Action.GET_COHORT_PROGRESS,
  progressMessages: progress('load', 'Cohort Progress'),
  validate: () => ({ isValid: true }),
};

export const SYNC_COHORTS_CONFIG: ActionConfig = {
  action: Action.SYNC_COHORTS,
  progressMessages: progress('sync', 'Cohorts'),
  validate: () => ({ isValid: true }),
};

// ============================================================================
// TEST ACTIONS
// ============================================================================

export const NO_OP_CONFIG: ActionConfig = {
  action: Action.NO_OP,
  progressMessages: {
    [AsyncRequestStatus.SUBMITTED]: 'No-op submitted...',
    [AsyncRequestStatus.IN_PROGRESS]: 'No-op in progress...',
    [AsyncRequestStatus.COMPLETED]: 'No-op complete',
    [AsyncRequestStatus.FAILED]: 'No-op failed',
  },
  validate: () => ({ isValid: true }), // No validation needed
};

// ============================================================================
// BOARD TEMPLATE ACTIONS
// ============================================================================

export const GET_BOARD_TEMPLATES_CONFIG: ActionConfig = {
  action: Action.GET_BOARD_TEMPLATES,
  progressMessages: progress('load', 'Templates'),
  validate: wrapValidator(validateGetBoardTemplatesInput),
};

export const CREATE_BOARD_TEMPLATE_CONFIG: ActionConfig = {
  action: Action.CREATE_BOARD_TEMPLATE,
  progressMessages: progress('create', 'Template'),
  validate: wrapValidator(validateCreateBoardTemplateInput),
  executeSideEffect: async () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.boardTemplates.all(),
    });
  },
};

export const UPDATE_BOARD_TEMPLATE_CONFIG: ActionConfig = {
  action: Action.UPDATE_BOARD_TEMPLATE,
  progressMessages: progress('update', 'Template'),
  validate: wrapValidator(validateUpdateBoardTemplateInput),
  executeSideEffect: async () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.boardTemplates.all(),
    });
  },
};

export const SAVE_BOARD_AS_TEMPLATE_CONFIG: ActionConfig = {
  action: Action.SAVE_BOARD_AS_TEMPLATE,
  progressMessages: progress('save', 'Template'),
  validate: wrapValidator(validateSaveBoardAsTemplateInput),
  executeSideEffect: async () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.boardTemplates.all(),
    });
  },
};

export const CREATE_BOARD_FROM_TEMPLATE_CONFIG: ActionConfig = {
  action: Action.CREATE_BOARD_FROM_TEMPLATE,
  progressMessages: progress('create', 'Board'),
  validate: wrapValidator(validateCreateBoardFromTemplateInput),
  executeSideEffect: async () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.board.all() });
    queryClient.invalidateQueries({
      queryKey: queryKeys.boardTemplates.all(),
    });
  },
};

// ============================================================================
// BOARD MANAGEMENT ACTIONS
// ============================================================================

export const UPDATE_BOARD_CONFIG: ActionConfig = {
  action: Action.UPDATE_BOARD,
  progressMessages: progress('update', 'Board'),
  validate: wrapValidator(validateUpdateBoardInput),
  executeSideEffect: async () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.board.all() });
  },
};

// ============================================================================
// CONFIGURATION ACTIONS
// ============================================================================

export const GET_ORG_CONFIGURATIONS_CONFIG: ActionConfig = {
  action: Action.GET_ORG_CONFIGURATIONS,
  progressMessages: progress('load', 'Configurations'),
  validate: wrapValidator(validateGetOrgConfigurationsInput),
};

export const UPDATE_ORG_CONFIGURATION_CONFIG: ActionConfig = {
  action: Action.UPDATE_ORG_CONFIGURATION,
  progressMessages: progress('update', 'Configuration'),
  validate: wrapValidator(validateUpdateOrgConfigurationInput),
  executeSideEffect: async () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.orgConfigurations.all(),
    });
  },
};

export const UPDATE_ORG_PROFILE_PICTURE_CONFIG: ActionConfig = {
  action: Action.UPDATE_ORG_PROFILE_PICTURE,
  progressMessages: progress('update', 'Organization profile picture'),
  validate: wrapValidator(validateUpdateOrgProfilePictureInput),
  executeSideEffect: async () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.allOrgs.all() });
  },
};

export const UPDATE_ORGANIZATION_CONFIG: ActionConfig = {
  action: Action.UPDATE_ORGANIZATION,
  progressMessages: progress('update', 'Organization'),
  validate: wrapValidator(validateUpdateOrganizationInput),
  executeSideEffect: async () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.allOrgs.all() });
  },
};

export const LIST_ORG_PROFILE_PICTURES_CONFIG: ActionConfig<
  unknown,
  unknown,
  ListOrgProfilePicturesOutput
> = {
  action: Action.LIST_ORG_PROFILE_PICTURES,
  progressMessages: {
    [AsyncRequestStatus.SUBMITTED]: 'Loading organization pictures...',
    [AsyncRequestStatus.IN_PROGRESS]: 'Fetching from storage...',
    [AsyncRequestStatus.COMPLETED]: 'Organization pictures loaded',
    [AsyncRequestStatus.FAILED]: 'Failed to load organization pictures',
  },
  validate: wrapValidator(validateListOrgProfilePicturesInput),
  executeSideEffect: async (data: ListOrgProfilePicturesOutput) => {
    StorageService.set(StorageKey.ORG_PROFILE_PICTURES, data.pictures);
    queryClient.setQueryData(queryKeys.orgProfilePictures.all(), data);
  },
};

// ============================================================================
// CENTRAL REGISTRY
// ============================================================================

/**
 * Central action config registry
 * Maps Action enum to ActionConfig
 */
export const ACTION_CONFIGS: Record<Action, ActionConfig<any, any, any>> = {
  // Auth
  [Action.LOGIN]: LOGIN_CONFIG,
  [Action.LOGOUT]: LOGOUT_CONFIG,

  // User Management
  [Action.CREATE_USER]: CREATE_USER_CONFIG,
  [Action.DELETE_USER]: DELETE_USER_CONFIG,
  [Action.GET_PREFERENCES]: GET_PREFERENCES_CONFIG,
  [Action.GET_ORGANIZATION_USERS]: GET_ORGANIZATION_USERS_CONFIG,
  [Action.SEARCH_USERS]: SEARCH_USERS_CONFIG,
  [Action.RESET_USER_PASSWORD]: RESET_USER_PASSWORD_CONFIG,
  [Action.UPDATE_USER]: UPDATE_USER_CONFIG,
  [Action.UPDATE_USER_PREFERENCES]: UPDATE_USER_PREFERENCES_CONFIG,
  [Action.UPDATE_PROFILE_PICTURE]: UPDATE_PROFILE_PICTURE_CONFIG,

  // Storage
  [Action.GET_PRESIGNED_UPLOAD_URL]: GET_PRESIGNED_UPLOAD_URL_CONFIG,
  [Action.LIST_PROFILE_PICTURES]: LIST_PROFILE_PICTURES_CONFIG,
  [Action.LIST_BOARD_BACKGROUNDS]: LIST_BOARD_BACKGROUNDS_CONFIG,

  // Organization
  [Action.CREATE_ORGANIZATION]: CREATE_ORGANIZATION_CONFIG,
  [Action.DELETE_ORGANIZATION]: DELETE_ORGANIZATION_CONFIG,
  [Action.GET_ALL_ORGANIZATIONS]: GET_ALL_ORGANIZATIONS_CONFIG,
  [Action.GET_ORG_CONFIGURATIONS]: GET_ORG_CONFIGURATIONS_CONFIG,
  [Action.RESTORE_ORGANIZATION]: RESTORE_ORGANIZATION_CONFIG,
  [Action.SWITCH_ORGANIZATION]: SWITCH_ORGANIZATION_CONFIG,
  [Action.UPDATE_ORGANIZATION]: UPDATE_ORGANIZATION_CONFIG,
  [Action.UPDATE_ORG_CONFIGURATION]: UPDATE_ORG_CONFIGURATION_CONFIG,
  [Action.UPDATE_ORG_PROFILE_PICTURE]: UPDATE_ORG_PROFILE_PICTURE_CONFIG,
  [Action.LIST_ORG_PROFILE_PICTURES]: LIST_ORG_PROFILE_PICTURES_CONFIG,

  // Board
  [Action.GET_BOARD]: GET_BOARD_CONFIG,
  [Action.GET_BOARD_PREFERENCES]: GET_BOARD_PREFERENCES_CONFIG,
  [Action.GET_ALL_BOARDS]: GET_ALL_BOARDS_CONFIG,
  [Action.GET_TASK]: GET_CARD_CONFIG,
  [Action.GET_TASK_ASSIGNMENTS]: GET_CARD_ASSIGNMENTS_CONFIG,
  [Action.GET_TASK_RELATIONS]: GET_CARD_RELATIONS_CONFIG,
  [Action.CREATE_BOARD]: CREATE_BOARD_CONFIG,
  [Action.CREATE_BOARD_STATUS]: CREATE_BOARD_STATUS_CONFIG,
  [Action.CREATE_TASK]: CREATE_CARD_CONFIG,
  [Action.CREATE_TASK_RELATION]: CREATE_CARD_RELATION_CONFIG,
  [Action.DELETE_BOARD]: DELETE_BOARD_CONFIG,
  [Action.DELETE_BOARD_STATUS]: DELETE_BOARD_STATUS_CONFIG,
  [Action.DELETE_TASK]: DELETE_CARD_CONFIG,
  [Action.DELETE_TASK_RELATION]: DELETE_CARD_RELATION_CONFIG,
  [Action.RESTORE_BOARD]: RESTORE_BOARD_CONFIG,
  [Action.RESTORE_BOARD_STATUS]: RESTORE_BOARD_STATUS_CONFIG,
  [Action.RESTORE_TASK]: RESTORE_CARD_CONFIG,
  [Action.UPDATE_TASK]: UPDATE_CARD_CONFIG,
  [Action.UPDATE_TASK_STATUS]: UPDATE_CARD_STATUS_CONFIG,
  [Action.UPDATE_BOARD_BACKGROUND]: UPDATE_BOARD_BACKGROUND_CONFIG,
  [Action.UPDATE_BOARD_STATUS_CONFIG]: UPDATE_BOARD_STATUS_CONFIG_CONFIG,
  [Action.CREATE_COMMENT]: CREATE_COMMENT_CONFIG,
  [Action.GET_COMMENTS]: GET_COMMENTS_CONFIG,

  // Board Management
  [Action.UPDATE_BOARD]: UPDATE_BOARD_CONFIG,

  // Board Templates
  [Action.GET_BOARD_TEMPLATES]: GET_BOARD_TEMPLATES_CONFIG,
  [Action.CREATE_BOARD_TEMPLATE]: CREATE_BOARD_TEMPLATE_CONFIG,
  [Action.CREATE_BOARD_FROM_TEMPLATE]: CREATE_BOARD_FROM_TEMPLATE_CONFIG,
  [Action.DELETE_BOARD_TEMPLATE]: DELETE_BOARD_TEMPLATE_CONFIG,
  [Action.RESTORE_BOARD_TEMPLATE]: RESTORE_BOARD_TEMPLATE_CONFIG,
  [Action.SAVE_BOARD_AS_TEMPLATE]: SAVE_BOARD_AS_TEMPLATE_CONFIG,
  [Action.UPDATE_BOARD_TEMPLATE]: UPDATE_BOARD_TEMPLATE_CONFIG,

  // People
  [Action.GET_USER_PROFILE]: GET_USER_PROFILE_CONFIG,

  // Dashboard
  [Action.GET_DASHBOARD_STATS]: GET_DASHBOARD_STATS_CONFIG,
  [Action.GET_LEARNING_PROGRESS_STATS]: GET_VELOCITY_STATS_CONFIG,
  [Action.GET_MY_PROGRESS]: GET_ACTIVITY_HEATMAP_CONFIG,

  // Notifications
  [Action.GET_NOTIFICATIONS]: GET_NOTIFICATIONS_CONFIG,
  [Action.MARK_NOTIFICATION_READ]: MARK_NOTIFICATION_READ_CONFIG,

  // Scheduled Jobs
  [Action.GET_SCHEDULED_JOBS]: GET_SCHEDULED_JOBS_CONFIG,
  [Action.CREATE_SCHEDULED_JOB]: CREATE_SCHEDULED_JOB_CONFIG,
  [Action.UPDATE_SCHEDULED_JOB]: UPDATE_SCHEDULED_JOB_CONFIG,
  [Action.RUN_SCHEDULED_JOB]: RUN_SCHEDULED_JOB_CONFIG,
  [Action.GET_JOB_RUN]: GET_JOB_RUN_CONFIG,
  [Action.GET_JOB_RUNS]: GET_JOB_RUNS_CONFIG,
  [Action.CANCEL_JOB_RUN]: CANCEL_JOB_RUN_CONFIG,

  // User Groups
  [Action.CREATE_USER_GROUP]: CREATE_USER_GROUP_CONFIG,
  [Action.DELETE_USER_GROUP]: DELETE_USER_GROUP_CONFIG,
  [Action.GET_USER_GROUPS]: GET_USER_GROUPS_CONFIG,
  [Action.ADD_USER_GROUP_MEMBER]: ADD_USER_GROUP_MEMBER_CONFIG,
  [Action.BULK_ADD_USER_GROUP_MEMBERS]: BULK_ADD_USER_GROUP_MEMBERS_CONFIG,
  [Action.REMOVE_USER_GROUP_MEMBER]: REMOVE_USER_GROUP_MEMBER_CONFIG,
  [Action.GET_USER_GROUP_MEMBERS]: GET_USER_GROUP_MEMBERS_CONFIG,
  [Action.GET_USER_GROUP_MEMBERS_BULK]: GET_USER_GROUP_MEMBERS_BULK_CONFIG,

  // Cohorts
  [Action.GET_COHORT_BOARD]: GET_COHORT_BOARD_CONFIG,
  [Action.GET_COHORT_PROGRESS]: GET_COHORT_PROGRESS_CONFIG,
  [Action.SYNC_COHORTS]: SYNC_COHORTS_CONFIG,

  // Test
  [Action.NO_OP]: NO_OP_CONFIG,
};

/**
 * Get action config by action type
 * @param action Action identifier
 * @returns Action configuration
 * @throws Error if action not found in registry
 */
export function getActionConfig<
  TInput = unknown,
  TBackend = unknown,
  TOutput = unknown,
>(action: Action): ActionConfig<TInput, TBackend, TOutput> {
  const config = ACTION_CONFIGS[action];

  if (!config) {
    throw new Error(`Action config not found for action: ${action}`);
  }

  return config as ActionConfig<TInput, TBackend, TOutput>;
}
